import React, { useState, useEffect, useCallback } from 'react';
import { getOrganizations, updateOrganization } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import {
  Building2,
  ChevronRight,
  ChevronDown,
  Network,
  Edit2,
  Save,
  Loader2,
  RefreshCw,
  Globe,
  MapPin,
  Building,
  Home,
  Users,
  ArrowRight,
  GitBranch,
  Layers,
  Move
} from 'lucide-react';

// Hierarchy level configuration
const HIERARCHY_LEVELS = [
  { value: 'national', label: 'National', icon: Globe, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  { value: 'regional', label: 'Regional', icon: Network, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  { value: 'state', label: 'State', icon: MapPin, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  { value: 'local', label: 'Local', icon: Building, color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
  { value: 'franchise', label: 'Franchise', icon: Building2, color: 'text-teal-500', bgColor: 'bg-teal-500/10' },
  { value: 'franchisee', label: 'Franchisee', icon: Home, color: 'text-pink-500', bgColor: 'bg-pink-500/10' },
];

const getLevelConfig = (level) => HIERARCHY_LEVELS.find(l => l.value === level) || HIERARCHY_LEVELS[3];

// Tree Node Component
const TreeNode = ({ org, level = 0, children, onEdit, onMove, expandedNodes, toggleExpand }) => {
  const levelConfig = getLevelConfig(org.level || 'local');
  const Icon = levelConfig.icon;
  const hasChildren = children && children.length > 0;
  const isExpanded = expandedNodes.includes(org.id);
  
  return (
    <div className="select-none">
      <div 
        className={`flex items-center gap-2 p-2 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors ${
          level === 0 ? '' : 'ml-6'
        }`}
        style={{ marginLeft: `${level * 24}px` }}
      >
        {hasChildren ? (
          <button 
            onClick={() => toggleExpand(org.id)}
            className="p-1 hover:bg-secondary rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}
        
        <div className={`p-2 rounded-lg ${levelConfig.bgColor}`}>
          <Icon className={`w-4 h-4 ${levelConfig.color}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{org.name}</span>
            <Badge className={`${levelConfig.bgColor} ${levelConfig.color} text-xs`}>
              {levelConfig.label}
            </Badge>
            {org.org_type && (
              <Badge variant="outline" className="text-xs">
                {org.org_type}
              </Badge>
            )}
          </div>
          {org.children_count > 0 && (
            <span className="text-xs text-muted-foreground">
              {org.children_count} child organization{org.children_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onEdit(org); }}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onMove(org); }}
          >
            <Move className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="border-l border-border ml-5">
          {children.map(child => (
            <TreeNode
              key={child.id}
              org={child}
              level={level + 1}
              children={child.children}
              onEdit={onEdit}
              onMove={onMove}
              expandedNodes={expandedNodes}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Hierarchy Stats Component
const HierarchyStats = ({ organizations }) => {
  const stats = {
    total: organizations.length,
    byLevel: {},
    byType: {},
    withChildren: 0,
    orphans: 0,
  };
  
  organizations.forEach(org => {
    const level = org.level || 'local';
    const type = org.org_type || 'sports';
    stats.byLevel[level] = (stats.byLevel[level] || 0) + 1;
    stats.byType[type] = (stats.byType[type] || 0) + 1;
    
    if (org.children_count > 0) stats.withChildren++;
    if (!org.parent_id && org.level !== 'national') stats.orphans++;
  });
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Orgs</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Globe className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.byLevel.national || 0}</p>
              <p className="text-xs text-muted-foreground">National</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <GitBranch className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.withChildren}</p>
              <p className="text-xs text-muted-foreground">Parent Orgs</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Layers className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.orphans}</p>
              <p className="text-xs text-muted-foreground">Standalone</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function OrganizationHierarchy() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, org: null });
  const [moveDialog, setMoveDialog] = useState({ open: false, org: null });
  const [saving, setSaving] = useState(false);
  
  // Edit form state
  const [editLevel, setEditLevel] = useState('');
  const [editParentId, setEditParentId] = useState('');
  
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getOrganizations();
      const orgs = response.data || [];
      
      // Count children for each org
      const childCounts = {};
      orgs.forEach(org => {
        if (org.parent_id) {
          childCounts[org.parent_id] = (childCounts[org.parent_id] || 0) + 1;
        }
      });
      
      // Add children_count to each org
      const orgsWithCounts = orgs.map(org => ({
        ...org,
        children_count: childCounts[org.id] || 0
      }));
      
      setOrganizations(orgsWithCounts);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);
  
  const buildTree = (orgs, parentId = null) => {
    return orgs
      .filter(org => org.parent_id === parentId)
      .map(org => ({
        ...org,
        children: buildTree(orgs, org.id)
      }));
  };
  
  const toggleExpand = (orgId) => {
    setExpandedNodes(prev => 
      prev.includes(orgId) 
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };
  
  const expandAll = () => {
    setExpandedNodes(organizations.map(org => org.id));
  };
  
  const collapseAll = () => {
    setExpandedNodes([]);
  };
  
  const handleEdit = (org) => {
    setEditLevel(org.level || 'local');
    setEditParentId(org.parent_id || '');
    setEditDialog({ open: true, org });
  };
  
  const handleMove = (org) => {
    setEditParentId(org.parent_id || '');
    setMoveDialog({ open: true, org });
  };
  
  const saveEdit = async () => {
    if (!editDialog.org) return;
    
    setSaving(true);
    try {
      await updateOrganization(editDialog.org.id, { 
        level: editLevel,
        parent_id: editParentId || null
      });
      toast.success('Organization updated');
      setEditDialog({ open: false, org: null });
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to update organization');
    } finally {
      setSaving(false);
    }
  };
  
  const saveMove = async () => {
    if (!moveDialog.org) return;
    
    setSaving(true);
    try {
      await updateOrganization(moveDialog.org.id, { 
        parent_id: editParentId || null
      });
      toast.success('Organization moved');
      setMoveDialog({ open: false, org: null });
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to move organization');
    } finally {
      setSaving(false);
    }
  };
  
  const tree = buildTree(organizations);
  const rootOrgs = tree.filter(org => !org.parent_id);
  
  if (!isSuperAdmin()) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Network className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold text-lg mb-2">Super Admin Access Required</h3>
          <p className="text-muted-foreground">
            Only Super Admins can manage organization hierarchy.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="organization-hierarchy">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
            <Network className="w-6 h-6" />
            Organization Hierarchy
          </h2>
          <p className="text-muted-foreground text-sm">
            Manage parent-child relationships and hierarchy levels
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
          <Button variant="outline" size="icon" onClick={fetchOrganizations}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <HierarchyStats organizations={organizations} />
      
      {/* Hierarchy Tree */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
            Organization Tree
          </CardTitle>
          <CardDescription>
            Click on organizations to expand/collapse, use edit to change level, or move to change parent
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            {rootOrgs.length > 0 ? (
              <div className="space-y-1">
                {rootOrgs.map(org => (
                  <TreeNode
                    key={org.id}
                    org={org}
                    level={0}
                    children={org.children}
                    onEdit={handleEdit}
                    onMove={handleMove}
                    expandedNodes={expandedNodes}
                    toggleExpand={toggleExpand}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No organizations found</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Hierarchy Legend */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
            Hierarchy Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {HIERARCHY_LEVELS.map(level => {
              const Icon = level.icon;
              return (
                <div key={level.value} className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${level.bgColor}`}>
                    <Icon className={`w-4 h-4 ${level.color}`} />
                  </div>
                  <span className="text-sm">{level.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, org: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
            <DialogDescription>
              Update hierarchy level and parent for "{editDialog.org?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Hierarchy Level</Label>
              <Select value={editLevel} onValueChange={setEditLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select level..." />
                </SelectTrigger>
                <SelectContent>
                  {HIERARCHY_LEVELS.map(level => {
                    const Icon = level.icon;
                    return (
                      <SelectItem key={level.value} value={level.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${level.color}`} />
                          {level.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Parent Organization</Label>
              <Select value={editParentId || "none"} onValueChange={(val) => setEditParentId(val === "none" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="No parent (root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root)</SelectItem>
                  {organizations
                    .filter(org => org.id !== editDialog.org?.id)
                    .map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, org: null })}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Move Dialog */}
      <Dialog open={moveDialog.open} onOpenChange={(open) => setMoveDialog({ open, org: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="w-5 h-5" />
              Move Organization
            </DialogTitle>
            <DialogDescription>
              Move "{moveDialog.org?.name}" under a different parent
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-secondary/30 border border-border">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-medium">{moveDialog.org?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Current parent: {
                      moveDialog.org?.parent_id 
                        ? organizations.find(o => o.id === moveDialog.org.parent_id)?.name || 'Unknown'
                        : 'None (root)'
                    }
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            
            <div className="space-y-2">
              <Label>New Parent Organization</Label>
              <Select value={editParentId || "none"} onValueChange={(val) => setEditParentId(val === "none" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="No parent (make root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (make root)</SelectItem>
                  {organizations
                    .filter(org => org.id !== moveDialog.org?.id)
                    .map(org => {
                      const levelConfig = getLevelConfig(org.level);
                      return (
                        <SelectItem key={org.id} value={org.id}>
                          <div className="flex items-center gap-2">
                            <Badge className={`${levelConfig.bgColor} ${levelConfig.color} text-xs`}>
                              {levelConfig.label}
                            </Badge>
                            {org.name}
                          </div>
                        </SelectItem>
                      );
                    })
                  }
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog({ open: false, org: null })}>
              Cancel
            </Button>
            <Button onClick={saveMove} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Move Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
